import React, { useMemo } from 'react';
import { AppIcon } from '../AppIcon';
import { iconService } from '../../services/iconService';
import './AppSquircle.scss';

export type AppSquircleSize = 'sm' | 'md' | 'lg';

interface AppSquircleProps {
  appId: string;
  size?: AppSquircleSize;
  className?: string;
}

// Icon pixel size inside the squircle for each size variant
const ICON_PX: Record<AppSquircleSize, number> = {
  sm: 12,
  md: 20,
  lg: 26,
};

/**
 * Renders an app icon inside a colored squircle (superellipse) background.
 * Used in list items, sidebar dropdowns, and app launchers.
 *
 * Uses AppIcon (the battle-tested renderer) with explicit pixel dimensions so
 * there is no dependency on dynamic Phosphor namespace lookups.
 */
export const AppSquircle: React.FC<AppSquircleProps> = ({ appId, size = 'sm', className = '' }) => {
  // getPhosphorAppIcon reads only the static APP_PHOSPHOR_ICONS map — it always
  // returns a symbolic (Phosphor) icon regardless of any image icon stored in the
  // app registry / backend (e.g. /icons/drive.png). This is intentional: squircles
  // need a scalable icon that can be tinted white, not a raster image.
  const iconConfig = useMemo(() => (
    iconService.getPhosphorAppIcon(appId) ?? {
      type: 'phosphor' as const,
      phosphorIcon: 'AppWindow',
      phosphorWeight: 'duotone' as const,
      color: '#6366f1',
      metadata: { source: 'default' as const, isAppIcon: false },
    }
  ), [appId]);

  const appColor = (iconConfig as any).color || '#6366f1';
  const px = ICON_PX[size];

  // Strip color so the icon inherits white from the squircle CSS instead of rendering
  // in the brand color (which would be invisible against the tinted background).
  const iconForSquircle = useMemo(() => ({
    ...iconConfig,
    color: undefined,
    metadata: { ...iconConfig.metadata, isAppIcon: false },
  }), [iconConfig]);

  return (
    <span
      className={`app-squircle app-squircle--${size} ${className}`}
      style={{ '--app-color': appColor } as React.CSSProperties}
    >
      <AppIcon icon={iconForSquircle} width={px} height={px} />
    </span>
  );
};

export default AppSquircle;
